import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Edit, X, Plus, Save } from 'lucide-react'
import { spkService } from '../services/spkService'
import { masterService } from '../services/masterService'

function SPKEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [schools, setSchools] = useState([])
  
  const [formData, setFormData] = useState({
    judul: '',
    kode: '',
    jenjang: 'SMP',
    qty: '',
    deadline: '',
    priority: 'normal',
  })
  const [customSchools, setCustomSchools] = useState([])
  const [newCustom, setNewCustom] = useState({ school_code: '', qty: '', custom_type: 'cover_only' })

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      const [spkRes, schoolRes] = await Promise.all([
        spkService.getById(id),
        masterService.getSchools()
      ])
      
      if (spkRes.success && spkRes.data.spk) {
        const spk = spkRes.data.spk
        setFormData({
          judul: spk.judul || '',
          kode: spk.kode || '',
          jenjang: spk.jenjang || 'SMP',
          qty: spk.qty || '',
          deadline: spk.deadline ? spk.deadline.split('T')[0] : '',
          priority: spk.priority || 'normal',
        })
        setCustomSchools(spk.customSchools?.map(cs => ({
          school_code: cs.school_code,
          school_name: cs.school_name,
          qty: cs.qty,
          custom_type: cs.custom_type
        })) || [])
      }
      
      if (schoolRes.success) {
        setSchools(schoolRes.data.schools || [])
      }
    } catch (err) {
      console.error('Error loading SPK:', err)
      setError('Gagal memuat data SPK')
    } finally {
      setLoading(false)
    }
  }

  const addCustomSchool = () => {
    if (!newCustom.school_code || !newCustom.qty) return
    const school = schools.find(s => s.code === newCustom.school_code)
    setCustomSchools(prev => [...prev, {
      ...newCustom,
      school_name: school?.name || newCustom.school_code
    }])
    setNewCustom({ school_code: '', qty: '', custom_type: 'cover_only' })
  }

  const removeCustomSchool = (index) => {
    setCustomSchools(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.judul || !formData.kode || !formData.qty || !formData.deadline) {
      setError('Semua field wajib harus diisi')
      return
    }
    
    setSaving(true)
    setError('')
    
    try {
      const payload = {
        ...formData,
        qty: parseInt(formData.qty),
        customSchools: customSchools.map(cs => ({
          school_code: cs.school_code,
          school_name: cs.school_name,
          qty: parseInt(cs.qty),
          custom_type: cs.custom_type,
        }))
      }
      
      const response = await spkService.update(id, payload)
      if (response.success) {
        navigate('/spk')
      } else {
        setError(response.message || 'Gagal menyimpan SPK')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan SPK')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Edit className="w-5 h-5 text-orange-500" />
            Edit SPK
          </h1>
          <button 
            onClick={() => navigate('/spk')} 
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Judul Buku *</label>
              <input
                type="text"
                value={formData.judul}
                onChange={(e) => setFormData(p => ({ ...p, judul: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kode Buku *</label>
              <input
                type="text"
                value={formData.kode}
                onChange={(e) => setFormData(p => ({ ...p, kode: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jenjang</label>
              <select
                value={formData.jenjang}
                onChange={(e) => setFormData(p => ({ ...p, jenjang: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="SD">SD</option>
                <option value="SMP">SMP</option>
                <option value="SMA">SMA</option>
                <option value="SMK">SMK</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
              <input
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData(p => ({ ...p, qty: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deadline *</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData(p => ({ ...p, deadline: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioritas</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(p => ({ ...p, priority: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Custom Schools */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-medium text-gray-700 mb-3">🏫 Sekolah Custom (opsional)</h3>
            
            <div className="grid grid-cols-12 gap-2 mb-3">
              <select
                value={newCustom.school_code}
                onChange={(e) => setNewCustom(p => ({ ...p, school_code: e.target.value }))}
                className="col-span-5 px-2 py-2 border rounded-lg text-sm"
              >
                <option value="">Pilih Sekolah</option>
                {schools.filter(s => s.status === 'active').map(s => (
                  <option key={s.code} value={s.code}>{s.code} - {s.name}</option>
                ))}
              </select>
              <input
                type="number"
                value={newCustom.qty}
                onChange={(e) => setNewCustom(p => ({ ...p, qty: e.target.value }))}
                className="col-span-2 px-2 py-2 border rounded-lg text-sm"
                placeholder="Qty"
              />
              <select
                value={newCustom.custom_type}
                onChange={(e) => setNewCustom(p => ({ ...p, custom_type: e.target.value }))}
                className="col-span-4 px-2 py-2 border rounded-lg text-sm"
              >
                <option value="cover_only">Cover Only</option>
                <option value="cover_sisipan">Cover + Sisipan</option>
              </select>
              <button 
                type="button"
                onClick={addCustomSchool}
                className="col-span-1 px-2 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {customSchools.length > 0 && (
              <div className="space-y-2">
                {customSchools.map((cs, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded-lg text-sm">
                    <span>
                      {cs.school_code} - {cs.qty} eks ({cs.custom_type === 'cover_only' ? 'cover_only' : 'cover_sisipan'})
                    </span>
                    <button 
                      type="button"
                      onClick={() => removeCustomSchool(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/spk')}
              className="flex-1 px-4 py-3 border rounded-lg hover:bg-gray-50 font-medium"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SPKEditPage
